import * as React from "react";
import { useHistory } from "react-router";
import { Loader } from "semantic-ui-react";
import { get, isEmpty } from "lodash";

import { PromptPreview } from "@/components/PromptPreview";
import { ConfirmationModal } from "@/components/Modals/ConfirmationModal";
import { PageTitle, PageActionProps } from "@/components/PageTitle";
import { CategoriesServices, CoachService, PromptServices } from "@/services";
import {
  URLS,
  useAccountSettings,
  capitalizeFormatter,
  useComplianceChecker,
  useQueryParam,
  useCategories,
  useSocialCoachSession,
} from "@/utils";
import {
  Media,
  Prompt,
  PromptPreviewObject,
  PromptStatus,
  PromptRequest,
  WebComponents,
  PromptType,
  PromptAccess,
  Category,
} from "@/interfaces";

import { PromptForm } from "./components/PromptForm";
import { modalReducer } from "./welcomeReducer";

import "./styles.scss";
import { validateFeedPromptWithMultimedia, validateMediasRequiremenstByPrompt } from "./validations";
import { EligibilityEditor } from "@/containers/Coach/EligibilityEditor";
import PromptStats from "./components/PromptStats";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import { CoachPostDialog } from "@/components/PostDialogs/CoachPostDialog";
import { useMemo } from "react";
import { Tabs } from "@/components/ui";
import { EligibilityCriterion } from "@/services/bedrock";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DesignPackageServices } from "@/services/bedrock/DesignPackageServices";
import { apiErrorMessage, isApiError } from "@/services/bedrock/apiError";
import { designKeys } from "@/services/bedrock/queryKeys";
import { UpdateDynamicPromptRequest } from "@/services/bedrock/dtos/DesignPackage";
import { useDynamicPromptsAccess } from "@/utils/useDynamicPromptsAccess";
import { UnpublishDialog } from "@/containers/Coach/PromptStudio/components/UnpublishDialog";
import { useDesignPackageMutations } from "@/containers/Coach/PromptStudio/hooks/useDesignPackageMutations";
import { designPreviewMedia, DynamicPromptDesign } from "./components/DynamicPromptDesign";
import { backToPromptState } from "@/containers/Coach/PromptStudio/backLink";
import { PlayerPickerDialog } from "@/components/PlayerPicker/PlayerPickerDialog";
import { ShareFlowHost } from "@/components/ShareDesignPost/ShareFlowHost";
import { useShareFlow } from "@/components/ShareDesignPost/ShareFlowProvider";

type Props = {
  promptId?: string;
};

const PROMPT_TABS = ["prompt-info", "eligibility", "stats"] as const;

const PromptFC: React.FC<Props> = ({ promptId }) => {
  const history = useHistory();
  const { coachId, isMainCoach, supportEmail } = useSocialCoachSession();
  const { hasWritePermission } = useAccountSettings();
  const promptWritePermission = hasWritePermission(WebComponents.PROMPTS);
  const { clearCategoryCache } = useCategories("LIBRARY");
  const dynamicAccess = useDynamicPromptsAccess();
  const queryClient = useQueryClient();

  const [showPostDialog, setShowPostDialog] = React.useState<boolean>(false);
  const [tab, setTab] = useQueryParam("tab", "prompt-info", [...PROMPT_TABS]);

  const [state, dispatch] = React.useReducer(modalReducer, {
    open: false,
    status: "ACTIVE",
    duplicated: false,
    action: "close",
  });

  // Status properties
  const [loading, setLoading] = React.useState<boolean>(false);
  const [editMode, setEditMode] = React.useState<boolean>(false);
  const [formErrorMessages, setFormErrorMessages] = React.useState<string[] | undefined>(undefined);
  const [uploadingContent, setUploadingContent] = React.useState(false);

  // Prompt info properties
  const [prompt, setPrompt] = React.useState<PromptPreviewObject | undefined>(undefined);
  const promptRef = React.useRef<PromptPreviewObject | undefined>(undefined);
  const [originalAccess, setOriginalAccess] = React.useState<PromptAccess | undefined>(undefined);
  const [promptAccess, setPromptAccess] = React.useState<PromptAccess | undefined>(undefined);
  const promptAccessRef = React.useRef<PromptAccess | undefined>(undefined);

  const handlePromptUpdate = React.useCallback((values: any) => {
    setPrompt(values);
    promptRef.current = values;
  }, []);
  const [originalPrompt, setOriginalPrompt] = React.useState<PromptPreviewObject | undefined>(undefined);
  const promptActive: boolean = prompt ? prompt!.status === "ACTIVE" : false;

  // The form's values replace `prompt` and don't carry the delivery mode, so it is read from the loaded prompt.
  const designPackageId =
    promptId !== "new" && originalPrompt?.deliveryMode === "DYNAMIC" ? originalPrompt.designPackageId : undefined;
  const isDynamic = !!designPackageId;
  // Dynamic prompts are saved through Prompt Studio's endpoints, which need Prompt Studio write access too.
  const writePermission = promptWritePermission && (!isDynamic || dynamicAccess.canEdit);
  const noDynamicAccess = isDynamic && !dynamicAccess.loading && !dynamicAccess.canView;
  // Only SocialCoach can turn the feature on, so the account owner is pointed there rather than at an admin.
  const accountLacksPromptStudio = noDynamicAccess && isMainCoach && !dynamicAccess.enabled;
  const [unpublishOpen, setUnpublishOpen] = React.useState(false);
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const shareFlow = useShareFlow();

  const packageQuery = useQuery({
    queryKey: designKeys.detail(designPackageId ?? 0),
    queryFn: () => DesignPackageServices.get(designPackageId!),
    enabled: isDynamic && dynamicAccess.canView,
    staleTime: 30_000,
  });
  const packageMutations = useDesignPackageMutations(designPackageId ?? null);
  // Review designs get a caption written for each post from its reviews; other designs start their
  // posts from this prompt's caption, like a regular prompt. Unknown until the design loads.
  const reviewDesign = packageQuery.data?.contextType === "review";

  // Blocked Words
  const { findBlockedWords } = useComplianceChecker({ coachId });
  const blockedWords: string[] = React.useMemo(() => {
    if (!prompt) {
      return [];
    }

    const content: string[] = [];

    if (prompt.message && prompt.message.length > 0) {
      content.push(prompt.message);
    }

    if (prompt.post?.media) {
      prompt.post.media.forEach(media => {
        media?.transcripts?.forEach(transcript => {
          if (transcript?.content && transcript?.content.length > 0) {
            content.push(transcript.content);
          }
        });
      });
    }

    return findBlockedWords(content);
  }, [prompt, findBlockedWords]);

  const getErrors = (): string[] | undefined => {
    const errors: any[] = [];
    const validMedias = validateMediasRequiremenstByPrompt(prompt);

    if (!prompt?.title) {
      errors.push("Title is required");
    }

    if (blockedWords && blockedWords?.length > 0) {
      errors.push(`Caption or media includes blocked words: ${blockedWords.join(", ")}`);
    }

    // Dynamic prompts have no media of their own; the design supplies it.
    if (isDynamic) {
      return errors.length > 0 ? errors : undefined;
    }

    if (!validMedias) {
      errors.push("Please edit the files below to meet the required file format and size requirements.");
    }

    if (validateFeedPromptWithMultimedia(prompt)) {
      const someFailed = prompt?.post?.media.some(
        media => !media.validAspectRatio && media.validAspectRatio !== undefined
      );
      if (someFailed) {
        errors.push(
          "One or more of the attached videos/images does not meet the specified file requirements. Please ensure that all attachments meet the requirements in the hover over next to the upload tool below."
        );
      }
    } else {
      prompt?.post?.media.forEach(media => {
        if (media.validAspectRatio == false) {
          const type = media.type.split("/")[0];
          const typeAfterDot = capitalizeFormatter(type);
          errors.push(
            prompt!.type === PromptType.FEED
              ? `This ${type} does not meet our aspect ratio requirements. ${typeAfterDot} must be 1:1, 4:5, 16:9, 1.91:1, 9:16 or 3:4.`
              : "This video does not meet our aspect ratio requirements. Video must be 9:16 or 4:5."
          );
        }

        if (prompt!.type === PromptType.REELS && media.validDimension == false) {
          errors.push(
            "This video does not meet the our minimum resolution requirements. Video must have a minimum resolution of 540 x 960 pixels (1080 x 1920 is recommended)."
          );
        }

        if (media.errors?.wrongBitrate) {
          errors.push("This video does not meet our bitrate requirements. Videos must have maximum bitrate of 25Mbps.");
        }
        if (media.errors?.wrongLength) {
          errors.push(
            "This video does not meet our video duration requirements. Videos must be between 3 and 90 seconds in duration."
          );
        }
        if (media.errors?.wrongSize) {
          const type = media.type.split("/")[0];
          const typeAfterDot = capitalizeFormatter(media.type.split("/")[0]);
          errors.push(`This ${type} does not meet our file size requirements. ${typeAfterDot} must be under 100MB.`);
        }
      });
    }

    return errors.length > 0 ? errors : undefined;
  };

  const loadAccessPreview = async (newCriterion: EligibilityCriterion[] = []): Promise<PromptAccess> => {
    if (promptRef.current?.promptId) {
      return await PromptServices.getAccessPreview(promptRef.current.promptId.toString(), newCriterion);
    } else {
      return await CoachService.getAccessPreview(coachId!, "LIBRARY", newCriterion);
    }
  };

  React.useEffect(() => {
    if (promptId === "new") {
      loadAccessPreview().then(access => {
        setOriginalAccess(access);
        setPromptAccess(access);
        promptAccessRef.current = access;
      });
      setEditMode(false);
    } else if (isFinite(Number(promptId))) {
      setEditMode(true);
      loadPrompt();
    } else {
    }
  }, [promptId]);

  const loadPrompt = () => {
    setLoading(true);
    return PromptServices.get(Number(promptId))
      .then((response: any) => {
        response = setMediaResourceIds(response);
        handlePromptUpdate(response);
        setOriginalAccess(response.promptAccess);
        setPromptAccess(response.promptAccess);
        promptAccessRef.current = response.promptAccess;
        setOriginalPrompt(response);
      })
      .catch(response => {
        if (response === "This prompt was deleted") {
          toast.info(response);
          history.push(URLS.coach.prompts);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  React.useEffect(() => {
    setFormErrorMessages(() => getErrors());
  }, [prompt, blockedWords]);

  /**
   * This is a temporary solution to the sortOrder vs. ID problem.
   * Media items need a sortOrder to compose the post correctly. The
   * ID is a constant, locally unique identifier to allow
   * react-beautiful-dnd Drag and Drop functionality to work correctly.
   *
   * @param fetchedPrompt the prompt we loaded from the database
   */
  const setMediaResourceIds = (fetchedPrompt: Prompt) => {
    let nextId = 0;
    const media = get(fetchedPrompt, "post.media", []);
    const newMedia = media.map((item: Media) => {
      nextId = item.id || nextId + 1;
      return {
        ...item,
        id: nextId,
        validAspectRatio: true,
        validDimension: true,
      };
    });
    return {
      ...fetchedPrompt,
      post: {
        ...fetchedPrompt.post,
        media: newMedia,
      },
    };
  };

  /** Creates the categories the coach typed in (no id or a negative one) and returns the list with real ids. */
  const resolveCategories = async (categories: Category[]): Promise<{ categories: Category[]; added: boolean }> => {
    const resolved: Category[] = [];
    let added = false;
    for (const c of categories) {
      if (!c.categoryId || Number(c.categoryId) < 0) {
        added = true;
        const result = await CategoriesServices.add(coachId!, { name: c.name.trim() }, "LIBRARY");
        resolved.push({ ...c, categoryId: result.categoryId });
      } else {
        resolved.push(c);
      }
    }

    if (added) {
      clearCategoryCache();
    }
    return { categories: resolved, added };
  };

  // Publish and the prompt update take the same fields, so both send everything the form holds.
  const dynamicPromptFields = async (currentPrompt: PromptPreviewObject): Promise<UpdateDynamicPromptRequest> => {
    const { categories, added } = await resolveCategories(currentPrompt.categories || []);
    // Keep the new ids in the form so a second save or a publish retry doesn't create them again.
    if (added) handlePromptUpdate({ ...currentPrompt, categories });
    const {
      title,
      message,
      richMessage,
      hashtags,
      allowHashtags,
      allowShortLink,
      smartLinkId,
      allowComplianceDisclaimer,
    } = currentPrompt;
    return {
      title,
      message,
      richMessage,
      hashtags,
      categoryIds: categories.map(c => Number(c.categoryId)),
      eligibilityCriteria: promptAccessRef.current?.eligibilityCriteria,
      allowHashtags,
      allowShortLink,
      smartLinkId: allowShortLink ? Number(smartLinkId) || null : undefined,
      allowComplianceDisclaimer,
    };
  };

  const saveDynamicPrompt = async (): Promise<boolean> => {
    const currentPrompt = promptRef.current;
    const currentPromptAccess = promptAccessRef.current;
    if (!currentPrompt?.title || blockedWords?.length > 0) {
      setFormErrorMessages(() => getErrors());
      return false;
    }
    try {
      setLoading(true);
      const fields = await dynamicPromptFields(currentPrompt);
      setFormErrorMessages(undefined);

      const pkg = await DesignPackageServices.updatePrompt(designPackageId!, fields);
      queryClient.setQueryData(designKeys.detail(pkg.id), pkg);
      queryClient.invalidateQueries({ queryKey: designKeys.all });

      setOriginalPrompt(original => ({ ...original!, ...promptRef.current }));
      setOriginalAccess(currentPromptAccess);
      setLoading(false);
      return true;
    } catch (e: unknown) {
      setLoading(false);
      setFormErrorMessages([apiErrorMessage(e, "Something went wrong. Try again.")]);
      return false;
    }
  };

  // Activating a dynamic prompt is a package publish; Prompt Studio owns that state.
  const publishDynamicPrompt = async () => {
    const currentPrompt = promptRef.current;
    if (!currentPrompt?.title || blockedWords?.length > 0) {
      setFormErrorMessages(() => getErrors());
      return;
    }
    setLoading(true);
    let fields: UpdateDynamicPromptRequest;
    try {
      fields = await dynamicPromptFields(currentPrompt);
    } catch (e: unknown) {
      // Creating a new category failed before the publish request started, so no mutation toast covers it.
      setLoading(false);
      toast.error(apiErrorMessage(e, "Something went wrong. Try again."));
      return;
    }
    try {
      await packageMutations.publish.mutateAsync(fields);
      queryClient.invalidateQueries({ queryKey: designKeys.detail(designPackageId!) });
      await loadPrompt();
    } catch (e: unknown) {
      setLoading(false);
      // Other publish errors are toasted by the mutation itself.
      if (isApiError(e, "not_completed")) {
        toast.error(apiErrorMessage(e, "This design isn't ready to publish yet."), {
          action: { label: "Edit in Prompt Studio", onClick: openInPromptStudio },
        });
      } else if (isApiError(e, "refine_in_progress")) {
        toast.error(apiErrorMessage(e, "Wait for the current change to finish."));
      }
    }
  };

  const unpublishDynamicPrompt = async ({ deleteScheduledPosts }: { deleteScheduledPosts: boolean }) => {
    await packageMutations.unpublish.mutateAsync({ deleteScheduledPosts });
    queryClient.invalidateQueries({ queryKey: designKeys.detail(designPackageId!) });
    await loadPrompt();
  };

  const openInPromptStudio = () => {
    // The tab lives in window.location (useQueryParam), so the router's location may not have it.
    history.push(
      URLS.coach.design.replace(":packageId", String(designPackageId)),
      backToPromptState(window.location.pathname + window.location.search)
    );
  };

  const openPostForPlayer = () => setPickerOpen(true);

  const savePrompt = async (status: PromptStatus): Promise<any> => {
    if (isDynamic) return saveDynamicPrompt();
    const currentPrompt = promptRef.current;
    const currentPromptAccess = promptAccessRef.current;
    try {
      // Segmentation validation
      const validMedia = validateMediasRequiremenstByPrompt(currentPrompt);
      const withMessage = get(currentPrompt, "message", "") + get(currentPrompt, "hashtags", "") !== "";
      const withMedia = get(currentPrompt, "post.media.length", 0);
      if (currentPrompt?.title && validMedia && (withMessage || withMedia > 0) && blockedWords?.length === 0) {
        const {
          title,
          message,
          richMessage,
          hashtags,
          allowHashtags,
          allowShortLink,
          smartLinkId,
          type,
          allowComplianceDisclaimer,
        } = currentPrompt;

        setLoading(true);

        const { categories } = await resolveCategories(currentPrompt?.categories || []);
        const categoryIds = categories.map(c => c.categoryId!.toString());

        const promptToSave: PromptRequest = {
          title,
          message,
          richMessage,
          hashtags,
          deliveryMode: "LIBRARY",
          categoryIds,
          status,
          allowHashtags,
          allowShortLink,
          allowComplianceDisclaimer,
          smartLinkId: allowShortLink ? smartLinkId : undefined,
          type,
          promptAccess: currentPromptAccess,
        };

        setFormErrorMessages(undefined);

        if (!isEmpty(currentPrompt!.media)) {
          promptToSave.mediaMessage = {
            media: currentPrompt!.media!,
            mediaGroupId: currentPrompt!.mediaMessage?.mediaGroupId,
            type: "VIDEO",
          };
        }

        if (currentPrompt!.post && !isEmpty(currentPrompt!.post!.media)) {
          promptToSave.post = currentPrompt!.post;
        }

        let savedPrompt;
        if (currentPrompt?.promptId) {
          promptToSave.promptId = currentPrompt?.promptId;
          savedPrompt = await PromptServices.update(promptToSave, coachId!);
        } else {
          savedPrompt = await PromptServices.create(promptToSave, coachId!);
        }

        const updatedPrompt = { ...currentPrompt, ...savedPrompt };
        handlePromptUpdate(updatedPrompt);
        setOriginalPrompt(updatedPrompt);
        setOriginalAccess(savedPrompt.promptAccess);

        if (savedPrompt?.promptId !== currentPrompt?.promptId) {
          const finalPath = URLS.coach.prompt.replace(":promptId", `${savedPrompt?.promptId}`);
          history.replace(finalPath);
        }
        setLoading(false);
        return true;
      } else {
        setFormErrorMessages(() => getErrors());
        return false;
      }
    } catch (e: any) {
      setFormErrorMessages([e]);
      return false;
    }
  };

  const disablePrompt = async (): Promise<any> => {
    try {
      if (originalPrompt?.title) {
        const categoryIds: string[] | undefined = originalPrompt!.categories!
          ? originalPrompt!.categories!.map(category => {
              return category.categoryId?.toString()!;
            })
          : undefined;

        const promptToSave: PromptRequest = {
          ...originalPrompt,
          title: originalPrompt!.title,
          categoryIds,
          categories: undefined,
          post: undefined,
          status: "DISABLED",
        };

        if (!isEmpty(originalPrompt!.media)) {
          promptToSave.mediaMessage = {
            media: originalPrompt!.media!,
            mediaGroupId: originalPrompt!.mediaMessage?.mediaGroupId,
            type: "VIDEO",
          };
        }

        if (originalPrompt!.post && !isEmpty(originalPrompt!.post!.media)) {
          promptToSave.post = originalPrompt!.post;
        }
        setLoading(true);
        setFormErrorMessages(undefined);

        await PromptServices.update(promptToSave, coachId!)
          .then(() => {
            history.push(URLS.coach.prompts);
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        setFormErrorMessages(() => getErrors());
      }
    } catch (e: any) {
      setFormErrorMessages([e]);
    }
  };

  const verifyAndSave = async (status: PromptStatus) => {
    if (status === "DISABLED") {
      if (window.confirm("Do you want to remove this prompt?")) {
        await disablePrompt();
      }
    } else if (isDynamic && status === "ACTIVE" && !promptActive) {
      await publishDynamicPrompt();
    } else {
      const saved = await savePrompt(status);
      if (saved) toast.success(isDynamic ? "Prompt saved." : "Prompt has been saved!");
    }
  };

  const verifySaveAndPost = async () => {
    const saved = await savePrompt("ACTIVE");
    if (saved) setShowPostDialog(true);
  };

  const closeAndGoToLibrary = () => {
    if (state.action === "cancel") {
      history.push(URLS.coach.prompts);
    } else {
      dispatch({ type: "close" });
    }
  };

  const cancelAction = () => {
    dispatch({
      type: "open_cancel",
      status: promptActive ? "ACTIVE" : "DRAFT",
    });
  };

  const confirmationModal = () => {
    const modalTitle = state.action === "cancel" ? "Do you want to save this prompt?" : "Replace Your Welcome Prompt";
    return (
      <ConfirmationModal
        title={!state.duplicated ? modalTitle : undefined}
        message={""}
        openConfirmationModal={state.open}
        onClose={() => dispatch({ type: "close" })}
        okHandler={async () => {
          dispatch({ type: "close" });
          const saved = await savePrompt(state.status);
          if (saved) toast.success("Prompt has been saved!");
        }}
        rejectHandler={closeAndGoToLibrary}
      />
    );
  };

  const onTabChangeHandler = (newTab: string) => {
    const errors = getErrors();
    if (errors) {
      setFormErrorMessages(errors);
    } else {
      setTab(newTab);
    }
  };

  const promptReady = useMemo(() => {
    if (isDynamic) return (prompt?.title?.length ?? 0) > 0;
    const validMedia = validateMediasRequiremenstByPrompt(prompt);
    const withMessage = get(prompt, "message", "") + get(prompt, "hashtags", "") !== "";
    const withMedia = get(prompt, "post.media.length", 0);
    return prompt?.title && validMedia && (withMessage || withMedia > 0) && blockedWords?.length === 0;
  }, [prompt, blockedWords?.length, isDynamic]);

  const primaryAction = useMemo(() => {
    if (writePermission) {
      return {
        text: promptActive ? "Save" : "Save & Publish",
        onClick: () => verifyAndSave("ACTIVE"),
        disabled: loading || uploadingContent || !promptReady,
      };
    }
    return undefined;
  }, [writePermission, uploadingContent, promptReady, loading, promptActive, isDynamic]);

  const secondaryAction = useMemo(() => {
    if (isDynamic) {
      // A bulk send would copy one media group to everyone, which is wrong for a design rendered per player.
      // Players only get a published design, so a disabled prompt has nothing to post.
      return dynamicAccess.canPostForPlayers && promptActive
        ? { text: "Post for a player", onClick: openPostForPlayer, disabled: loading }
        : undefined;
    }
    if (writePermission) {
      return {
        text: "Post / Send",
        onClick: () => verifySaveAndPost(),
        disabled: loading || uploadingContent || !promptReady,
      };
    }
    return undefined;
  }, [
    writePermission,
    uploadingContent,
    promptReady,
    loading,
    promptActive,
    isDynamic,
    dynamicAccess.canPostForPlayers,
  ]);

  const actions = useMemo(() => {
    const actionList: PageActionProps[] = [];
    if (isDynamic) {
      // Edit in Prompt Studio lives on the Design block in the form.
      // Unpublish follows the same rule as Save: Prompt Studio write and prompt library write.
      if (writePermission && promptActive) {
        actionList.push({
          text: "Unpublish",
          onClick: () => setUnpublishOpen(true),
          disabled: loading || !packageQuery.data,
        });
      }
      return actionList;
    }
    if (writePermission) {
      actionList.push({
        text: promptActive ? "Unpublish" : "Save as draft",
        onClick: () => verifyAndSave("DRAFT"),
        disabled: loading || uploadingContent,
      });
      if (prompt?.promptId) {
        actionList.push({
          text: "Delete",
          onClick: () => verifyAndSave("DISABLED"),
          disabled: loading || uploadingContent,
        });
      }
      return actionList;
    }
    return [];
  }, [
    writePermission,
    loading,
    uploadingContent,
    prompt?.promptId,
    promptActive,
    isDynamic,
    dynamicAccess.canView,
    dynamicAccess.canEdit,
    packageQuery.data,
  ]);

  const promptContent = () => {
    if (loading) return <Loader active size="large" />;

    return (
      <Tabs value={tab} onValueChange={onTabChangeHandler}>
        <Tabs.List className="mb-4" variant="menu">
          <Tabs.Trigger value={"prompt-info"}>Prompt Info</Tabs.Trigger>
          <Tabs.Trigger value={"eligibility"}>Eligibility</Tabs.Trigger>
          <Tabs.Trigger value={"stats"}>Performance</Tabs.Trigger>
        </Tabs.List>

        <Tabs.Content value="prompt-info">
          {!loading && (
            <div className="flex flex-row gap-10">
              <div className="grow pb-30">
                <PromptForm
                  disabled={!writePermission}
                  mediaSlot={
                    isDynamic ? (
                      <DynamicPromptDesign
                        package={packageQuery.data}
                        loading={packageQuery.isLoading}
                        error={packageQuery.isError}
                        onEdit={openInPromptStudio}
                      />
                    ) : undefined
                  }
                  captionNote={
                    isDynamic && reviewDesign ? (
                      <p className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
                        SocialCoach AI writes the caption for each post from the reviews in it. Players can edit it
                        before posting.
                      </p>
                    ) : undefined
                  }
                  modeEditable={!editMode}
                  prompt={prompt}
                  onContentLoading={setUploadingContent}
                  updatingHandler={handlePromptUpdate}
                  pageErrors={formErrorMessages}
                />
              </div>
              <div className="w-[350px] shrink-0">
                {prompt && isDynamic && (
                  <div className="flex flex-col gap-2">
                    <PromptPreview
                      title={prompt.title}
                      message={
                        reviewDesign
                          ? "Each post gets a caption written from its reviews."
                          : prompt.richMessage || prompt.message
                      }
                      hashtags={prompt.hashtags}
                      showAttached
                      showShortLink={prompt.allowShortLink}
                      showComplianceDisclaimer={prompt.allowComplianceDisclaimer}
                      media={designPreviewMedia(packageQuery.data)}
                    />
                    <p className="text-center text-xs text-slate-500">
                      Previews use sample data. Each post is personalized for the player who shares it.
                    </p>
                  </div>
                )}
                {prompt && !isDynamic && (
                  <PromptPreview
                    title={prompt.title}
                    message={prompt.richMessage || prompt.message}
                    hashtags={prompt.hashtags}
                    showAttached={!!prompt.post?.media}
                    showShortLink={prompt.allowShortLink}
                    showComplianceDisclaimer={prompt.allowComplianceDisclaimer}
                    media={get(prompt, "post.media", [])}
                  />
                )}
              </div>
            </div>
          )}
        </Tabs.Content>

        <Tabs.Content value={"eligibility"}>
          {/* For a dynamic prompt the design's publish dialog seeds these criteria; edits here win afterwards. */}
          {originalAccess && promptAccess && (
            <EligibilityEditor
              isNew={!prompt?.promptId}
              originalAccess={originalAccess}
              currentAccess={promptAccess}
              accessType={"LIBRARY"}
              updateHandler={async newCriteria => {
                const updatedAccess = { ...promptAccessRef.current!, eligibilityCriteria: newCriteria };
                setPromptAccess(updatedAccess);
                promptAccessRef.current = updatedAccess;

                const newAccess = await loadAccessPreview(newCriteria);
                setPromptAccess(newAccess);
                promptAccessRef.current = newAccess;
                return newAccess;
              }}
            />
          )}
        </Tabs.Content>

        <Tabs.Content value={"stats"}>
          <PromptStats prompt={prompt} />
        </Tabs.Content>
      </Tabs>
    );
  };

  return (
    <div>
      <PageTitle
        title={editMode ? "Edit Prompt" : "Add a Prompt"}
        actions={actions}
        primaryAction={primaryAction}
        secondaryAction={secondaryAction}
        hideSeparator
        breadcrumbs={[{ name: "Library", link: () => history.push(URLS.coach.prompts) }]}
      />
      {noDynamicAccess && (
        <div className="flex flex-col items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50/50 p-12 text-center">
          <Lock className="size-6 text-slate-400" aria-hidden="true" />
          {accountLacksPromptStudio ? (
            <>
              <p className="font-bold text-slate-800">Prompt Studio isn't turned on for your account.</p>
              <p className="text-sm text-slate-500">
                Contact{" "}
                <a className="text-primary underline" href={`mailto:${supportEmail}`}>
                  SocialCoach support
                </a>{" "}
                to turn it on.
              </p>
            </>
          ) : (
            <>
              <p className="font-bold text-slate-800">You don't have access to this prompt.</p>
              <p className="text-sm text-slate-500">Contact your admin to get access.</p>
            </>
          )}
        </div>
      )}
      {!noDynamicAccess && promptContent()}

      {confirmationModal()}
      {isDynamic && packageQuery.data && (
        <UnpublishDialog
          open={unpublishOpen}
          onOpenChange={setUnpublishOpen}
          pkg={packageQuery.data}
          onConfirm={unpublishDynamicPrompt}
        />
      )}
      {showPostDialog && prompt && (
        <CoachPostDialog open={showPostDialog} handleClose={() => setShowPostDialog(false)} prompt={prompt} />
      )}
      {isDynamic && prompt && (
        <PlayerPickerDialog
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          onSelect={player =>
            shareFlow.open({
              kind: "coach",
              source: "prompt",
              playerId: player.id,
              playerUserId: player.userId,
              designPackageId: designPackageId!,
              promptId: Number(prompt.promptId),
            })
          }
        />
      )}
    </div>
  );
};

// The share composer for posting this prompt's design for a player; the player is picked first.
export const PromptPage: React.FC<Props> = props => (
  <ShareFlowHost playerId={null} onBehalfOf>
    <PromptFC {...props} />
  </ShareFlowHost>
);
